import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../contexts/AuthContext';
// SQL Services
import { InventoryService, CreateInventoryItemDto } from './services/inventoryService';
import { WarehouseService } from './services/warehouseService';
import { useTranslation } from 'react-i18next';

interface QuickAddItemModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  /**
   * Tekli sekmedeki barkod alanına ön dolgu. Hızlı satışta okutulup bulunamayan
   * barkod buradan gelir; boş bırakılırsa barkod eskisi gibi otomatik üretilir.
   */
  initialBarcode?: string;
}

interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
  branchesId?: string | null;
}

interface BulkItemEntry {
  itemName: string;
  salePrice: number | '';
  quantity: number | '';
  barcode?: string;
}

const QuickAddItemModal: React.FC<QuickAddItemModalProps> = ({ open, onClose, branchId, initialBarcode }) => {
  const { t } = useTranslation('accounting');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { warehouses, userDetails } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);

  // Sekme 1: Tekli Hızlı Kayıt State'leri
  const [itemName, setItemName] = useState('');
  const [barcodeSingle, setBarcodeSingle] = useState('');
  const [salePrice, setSalePrice] = useState<number | ''>('');
  const [quantitySingle, setQuantitySingle] = useState<number | ''>(0);
  const [vatRate, setVatRate] = useState<number | ''>(20); // Default KDV
  const [vatStatus, setVatStatus] = useState('excluded'); // excluded or included
  const [departmentNo, setDepartmentNo] = useState<number | ''>(1); // Default departman no
  const [canBeNegativeSingle, setCanBeNegativeSingle] = useState(true);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);

  // Sekme 2: Toplu Hızlı Kayıt State'leri
  const [bulkWarehouseId, setBulkWarehouseId] = useState<string | null>(null);
  const [bulkVatRate, setBulkVatRate] = useState<number | ''>(20);
  const [bulkVatStatus, setBulkVatStatus] = useState('excluded');
  const [bulkDepartmentNo, setBulkDepartmentNo] = useState<number | ''>(1);
  const [canBeNegativeBulk, setCanBeNegativeBulk] = useState(true);
  const [bulkItemEntries, setBulkItemEntries] = useState<BulkItemEntry[]>(
    Array(10).fill({ itemName: '', salePrice: '', quantity: 0, barcode: '' })
  );

  // Veritabanı verileri için state'ler
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Para birimi seçenekleri için state (Sadece TRY)
  const saleCurrency = 'TRY'; // Sabit TRY

  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      // Hızlı satışta okutulup bulunamayan barkodla açıldıysa alanı tohumla
      setBarcodeSingle(initialBarcode ?? '');
      loadCompanyAndData().then(() => {
        setInitialLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBarcode]);

  const loadCompanyAndData = async (): Promise<string | null> => {
    try {
      const currentCompanyId = userDetails?.companyId;
      
      if (!currentCompanyId) {
        setInitialLoading(false);
        setError(t('stock.quickAdd.errors.companyNotFound'));
        return null;
      }
      
      setCompanyId(currentCompanyId);
      return currentCompanyId;
    } catch (err: any) {
      console.error('Şirket veya veri yüklenirken hata:', err);
      setInitialLoading(false);
      setError(t('stock.quickAdd.errors.loadDataFailed'));
      return null;
    }
  };

  // ✅ Varsayılan depo seçimi (warehouses yüklendiğinde)
  useEffect(() => {
    if (warehouses.length > 0) {
      let filteredWarehouses = warehouses;
      // Şube ID'sine göre filtreleme
      if (branchId) { 
        filteredWarehouses = warehouses.filter(warehouse => 
          warehouse.branchesId === branchId || warehouse.branchesId === null
        );
      }
      
      const defaultWarehouse = filteredWarehouses.find(w => w.isDefault);
      const firstWarehouse = filteredWarehouses.length > 0 ? filteredWarehouses[0] : null;

      if (activeTab === 0) { // Tekli kayıt için depo ayarı
        if (defaultWarehouse) {
          setWarehouseId(defaultWarehouse.id);
          setSelectedWarehouse(defaultWarehouse);
          console.log('✅ [QUICK ADD] Varsayılan depo seçildi (tekli):', defaultWarehouse.name);
        } else if (firstWarehouse) {
          setWarehouseId(firstWarehouse.id);
          setSelectedWarehouse(firstWarehouse);
          console.log('✅ [QUICK ADD] İlk depo seçildi (tekli):', firstWarehouse.name);
        } else {
          setWarehouseId(null);
          setSelectedWarehouse(null);
        }
      } else { // Toplu kayıt için depo ayarı
        if (defaultWarehouse) {
          setBulkWarehouseId(defaultWarehouse.id);
          setSelectedWarehouse(defaultWarehouse);
          console.log('✅ [QUICK ADD] Varsayılan depo seçildi (toplu):', defaultWarehouse.name);
        } else if (firstWarehouse) {
          setBulkWarehouseId(firstWarehouse.id);
          setSelectedWarehouse(firstWarehouse);
          console.log('✅ [QUICK ADD] İlk depo seçildi (toplu):', firstWarehouse.name);
        } else {
          setBulkWarehouseId(null);
          setSelectedWarehouse(null);
        }
      }
    }
  }, [warehouses, branchId, activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null); // Sekme değişiminde hataları temizle
    // ✅ Sekme değiştiğinde, varsayılan depo useEffect ile otomatik ayarlanıyor
    if (warehouses.length > 0) {
        const defaultWarehouse = warehouses.find(w => w.isDefault);
        const firstWarehouse = warehouses[0];
        if (newValue === 0) { // Tekli sekme aktif
            if (defaultWarehouse) setWarehouseId(defaultWarehouse.id);
            else if (firstWarehouse) setWarehouseId(firstWarehouse.id);
            else setWarehouseId(null);
        } else { // Toplu sekme aktif
            if (defaultWarehouse) setBulkWarehouseId(defaultWarehouse.id);
            else if (firstWarehouse) setBulkWarehouseId(firstWarehouse.id);
            else setBulkWarehouseId(null);
        }
        setSelectedWarehouse(defaultWarehouse || firstWarehouse || null);
    }
  };

  const handleBulkItemChange = (index: number, field: keyof BulkItemEntry, value: string | number) => {
    const updatedEntries = [...bulkItemEntries];
    const currentEntry = { ...updatedEntries[index] };

    if (field === 'salePrice' || field === 'quantity') {
        currentEntry[field] = value === '' ? '' : Number(value);
    } else {
        currentEntry[field] = value as string; 
    }
    updatedEntries[index] = currentEntry;
    setBulkItemEntries(updatedEntries);
  };

  const handleSave = async () => {
    if (!companyId) {
        setError(t('stock.quickAdd.errors.companyRetry'));
        return;
    }
    setLoading(true);
    setError(null);

    if (activeTab === 0) { // Tekli Hızlı Kayıt
      if (!itemName.trim() || !warehouseId || vatRate === '' || salePrice === '' || quantitySingle === '') {
        setError(t('stock.quickAdd.errors.requiredSingle'));
        setLoading(false);
        return;
      }
      
      const stockCode = `${itemName.substring(0, 3).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
      // Kullanıcı barkod girdiyse o kullanılır; hızlı satışta okutulan barkodun
      // ürüne bağlanması, aynı barkodun bir sonraki okutmada bulunmasını sağlar.
      const generatedBarcode = barcodeSingle.trim() || `HZL-${stockCode}`;
      
      // ✅ Depo stok bilgisi (ürün oluşturduktan sonra ayrı eklenecek)
      const warehouseStockQuantity = Number(quantitySingle || 0);
      const selectedWarehouseId = warehouseId!;
      
      // SQL API için DTO oluştur
      const newItemDto: CreateInventoryItemDto = {
        companyId: companyId!,
        name: itemName.trim(),
        barcode: generatedBarcode,
        stockCode: stockCode,
        categoryId: undefined,
        brandId: undefined,
        // ❌ KALDIRILDI: warehouseId ve quantity artık CreateWarehouseStockDto ile ayrı gönderilecek
        // warehouseId: warehouseId,
        // quantity: Number(quantitySingle || 0),
        
        // Stok bilgileri
        canBeNegative: canBeNegativeSingle,
        unit: 'adet',
        minStockLevel: undefined,
        maxStockLevel: undefined,
        
        // Alış fiyatları
        purchasePrice: undefined,
        purchaseCurrency: 'TRY',
        purchaseVendor: undefined,
        purchasePrice2: undefined,
        purchaseCurrency2: 'TRY',
        purchaseVendor2: undefined,
        purchasePrice3: undefined,
        purchaseCurrency3: 'TRY',
        purchaseVendor3: undefined,
        
        // Satış fiyatları
        salePrice: Number(salePrice),
        saleCurrency: 'TRY',
        saleCustomer: undefined,
        salePrice2: undefined,
        saleCurrency2: 'TRY',
        saleCustomer2: undefined,
        salePrice3: undefined,
        saleCurrency3: 'TRY',
        saleCustomer3: undefined,
        listPrice: undefined,
        listCurrency: 'TRY',
        
        // Vergi bilgileri
        vatRate: Number(vatRate),
        vatStatus: vatStatus,
        sctRate: undefined,
        sctStatus: undefined,
        
        // Diğer bilgiler
        description: 'Hızlı stok kaydı ile eklendi.',
        branchesId: branchId || undefined,
        departmentNo: departmentNo === '' ? undefined : Number(departmentNo),
        printerGroup: undefined,
        imageUrl: undefined,
        imageUrls: undefined,
        variants: undefined,
        isActive: true
      };
      
      try {
        console.log('🚀 SQL API ile hızlı ürün kaydediliyor:', newItemDto);
        const createdItem = await InventoryService.createItem(newItemDto);
        console.log('✅ Hızlı ürün başarıyla kaydedildi!', createdItem);
        handleClose(true);
      } catch (err: any) {
        console.error('❌ Hızlı ürün kaydedilirken hata:', err);
        setError(`${t('stock.quickAdd.errors.quickSaveFailed')}: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    } else { // Toplu Hızlı Kayıt
      if (!bulkWarehouseId || bulkVatRate === '' ) {
        setError(t('stock.quickAdd.errors.requiredBulk'));
        setLoading(false);
        return;
      }

      const itemsToSave = bulkItemEntries.filter(entry => entry.itemName.trim() !== '' && entry.salePrice !== '' && entry.quantity !== '');
      if (itemsToSave.length === 0) {
        setError(t('stock.quickAdd.errors.minOneItem'));
        setLoading(false);
        return;
      }

      let successCount = 0;
      let failedCount = 0;
      
      for (const item of itemsToSave) {
        const stockCode = `${item.itemName.substring(0, 3).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
        const generatedBarcode = item.barcode?.trim() || `HZLTOPLU-${stockCode}`;
        
        // ✅ Depo stok bilgisi (ürün oluşturduktan sonra ayrı eklenecek)
        const warehouseStockQuantity = Number(item.quantity || 0);
        
        // SQL API için DTO oluştur
        const newItemDto: CreateInventoryItemDto = {
          companyId: companyId!,
          name: item.itemName.trim(),
          barcode: generatedBarcode,
          stockCode: stockCode,
          categoryId: undefined,
          brandId: undefined,
          // ❌ KALDIRILDI: warehouseId ve quantity artık CreateWarehouseStockDto ile ayrı gönderilecek
          // warehouseId: bulkWarehouseId,
          // quantity: Number(item.quantity || 0),
          
          // Stok bilgileri
          canBeNegative: canBeNegativeBulk,
          unit: 'adet',
          minStockLevel: undefined,
          maxStockLevel: undefined,
          
          // Alış fiyatları
          purchasePrice: undefined,
          purchaseCurrency: 'TRY',
          purchaseVendor: undefined,
          purchasePrice2: undefined,
          purchaseCurrency2: 'TRY',
          purchaseVendor2: undefined,
          purchasePrice3: undefined,
          purchaseCurrency3: 'TRY',
          purchaseVendor3: undefined,
          
          // Satış fiyatları
          salePrice: Number(item.salePrice),
          saleCurrency: 'TRY',
          saleCustomer: undefined,
          salePrice2: undefined,
          saleCurrency2: 'TRY',
          saleCustomer2: undefined,
          salePrice3: undefined,
          saleCurrency3: 'TRY',
          saleCustomer3: undefined,
          listPrice: undefined,
          listCurrency: 'TRY',
          
          // Vergi bilgileri
          vatRate: Number(bulkVatRate),
          vatStatus: bulkVatStatus,
          sctRate: undefined,
          sctStatus: undefined,
          
          // Diğer bilgiler
          description: 'Toplu hızlı stok kaydı ile eklendi.',
          branchesId: branchId || undefined,
          departmentNo: bulkDepartmentNo === '' ? undefined : Number(bulkDepartmentNo),
          printerGroup: undefined,
          imageUrl: undefined,
          imageUrls: undefined,
          variants: undefined,
          isActive: true
        };
        
        try {
          console.log(`🚀 SQL API ile toplu ürün kaydediliyor (${successCount + 1}/${itemsToSave.length}):`, item.itemName);
          await InventoryService.createItem(newItemDto);
          successCount++;
          console.log(`✅ ${item.itemName} başarıyla kaydedildi!`);
        } catch (err: any) {
          failedCount++;
          console.error(`❌ ${item.itemName} kaydedilirken hata:`, err);
          setError(t('stock.quickAdd.errors.bulkPartialFailed', {
            total: itemsToSave.length,
            success: successCount,
            failed: failedCount,
            error: err.response?.data?.message || err.message
          }));
          setLoading(false);
          return; // Hata durumunda döngüden çık
        }
      }
      
      // Tüm kayıtlar başarılıysa modalı kapat
      if (successCount === itemsToSave.length) {
        console.log(`✅ Toplu kayıt tamamlandı: ${successCount} ürün başarıyla kaydedildi!`);
        handleClose(true);
      }
      setLoading(false);
    }
  };

  const handleClose = (resetAllForms = true) => {
    if (resetAllForms) {
        // Tekli form state'lerini sıfırla
        setItemName('');
        setBarcodeSingle('');
        setSalePrice('');
        setQuantitySingle(0);
        setVatRate(20);
        setVatStatus('excluded');
        setDepartmentNo(1);
        setCanBeNegativeSingle(true);
        setWarehouseId(null); // Seçili depoyu da sıfırla

        // Toplu form state'lerini sıfırla
        setBulkWarehouseId(null); // Seçili depoyu da sıfırla
        setBulkVatRate(20);
        setBulkVatStatus('excluded');
        setBulkDepartmentNo(1);
        setCanBeNegativeBulk(true);
        setBulkItemEntries(Array(10).fill({ itemName: '', salePrice: '', quantity: 0, barcode: '' }));
        
        // Ortak depo seçimi state'ini sıfırla
        setSelectedWarehouse(null); 
    }
    setActiveTab(0); // Her kapanışta ilk sekmeye dön
    setError(null);
    onClose();
  };

  const getCurrencySymbol = (code: string): string => {
    if (code === 'TRY') return '₺';
    return code;
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => handleClose(false)} // Sadece modalı kapat, formları sıfırlama (kullanıcı X'e basarsa)
      maxWidth="md" // Modal genişliğini artırdık
      fullWidth
      sx={{ '& .MuiDialog-paper': { borderRadius: 2, minHeight: '75vh' } }} // Minimum yükseklik
    >
      <DialogTitle
        sx={{
          bgcolor: activeTab === 0 ? 'secondary.main' : 'primary.main', // Sekmeye göre renk
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2
        }}
      >
        <Typography variant="h6">
          {activeTab === 0 ? t('stock.quickAdd.titleSingle') : t('stock.quickAdd.titleBulk')}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={() => handleClose(false)} aria-label={t('stock.common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
        <Tab label={t('stock.quickAdd.tabSingle')} />
        <Tab label={t('stock.quickAdd.tabBulk')} />
      </Tabs>

      <DialogContent sx={{ p: 2, mt: 1, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt:1 }}>
            {error}
          </Alert>
        )}

        {initialLoading && activeTab === 0 && !warehouseId && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        )}
        {initialLoading && activeTab === 1 && !bulkWarehouseId && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        )}

        {/* Tekli Hızlı Kayıt Sekmesi */} 
        {activeTab === 0 && !initialLoading && (
          <Grid container spacing={2} sx={{mt:0.5}}>
            <Grid item xs={12}>
              <TextField fullWidth required size="small" label={t('stock.quickAdd.fields.productName')} value={itemName} onChange={(e) => setItemName(e.target.value)} error={!itemName.trim()} autoFocus />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('stock.quickAdd.fields.barcode')} value={barcodeSingle} onChange={(e) => setBarcodeSingle(e.target.value)} helperText={t('stock.quickAdd.fields.barcodeHint')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required size="small" type="number" label={t('stock.quickAdd.fields.salePrice')} value={salePrice} onChange={(e) => setSalePrice(e.target.value === '' ? '' : Number(e.target.value))} error={salePrice === ''} InputProps={{ endAdornment: (<InputAdornment position="end">{getCurrencySymbol(saleCurrency)}</InputAdornment>)}} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required size="small" type="number" label={t('stock.quickAdd.fields.stockQuantity')} value={quantitySingle} onChange={(e) => setQuantitySingle(e.target.value === '' ? '' : Number(e.target.value))} error={quantitySingle === ''} InputProps={{ inputProps: { min: 0 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!warehouseId && !initialLoading}>
                <InputLabel>{t('stock.quickAdd.fields.warehouse')}</InputLabel>
                <Select value={warehouseId || ''} onChange={(e) => { const whId = e.target.value as string; setWarehouseId(whId); const wh = warehouses.find(w => w.id === whId); setSelectedWarehouse(wh || null);}} label={t('stock.quickAdd.fields.warehouse')}>
                  {warehouses.map((wh) => (<MenuItem key={wh.id} value={wh.id}>{wh.name} {wh.isDefault && `(${t('stock.quickAdd.default')})`}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required size="small" type="number" label={t('stock.quickAdd.fields.vatRate')} value={vatRate} onChange={(e) => setVatRate(e.target.value === '' ? '' : Number(e.target.value))} InputProps={{endAdornment: <InputAdornment position="end">%</InputAdornment>}} error={vatRate === ''} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('stock.quickAdd.fields.vatStatus')}</InputLabel>
                <Select value={vatStatus} onChange={(e) => setVatStatus(e.target.value)} label={t('stock.quickAdd.fields.vatStatus')}>
                  <MenuItem value="included">{t('stock.quickAdd.vatIncluded')}</MenuItem>
                  <MenuItem value="excluded">{t('stock.quickAdd.vatExcluded')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label={t('stock.quickAdd.fields.departmentNo')} value={departmentNo} onChange={(e) => {const value = e.target.value; if (value === '' || (Number(value) >= 1 && Number(value) <= 10)) {setDepartmentNo(value === '' ? '' : Number(value));}}} InputProps={{inputProps: { min: 1, max: 10, step: 1}}} helperText={t('stock.quickAdd.fields.departmentHint')} />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <FormControlLabel control={<Checkbox checked={canBeNegativeSingle} onChange={(e) => setCanBeNegativeSingle(e.target.checked)} />} label={t('stock.quickAdd.fields.canBeNegative')} />
            </Grid>
          </Grid>
        )}

        {/* Toplu Hızlı Kayıt Sekmesi */} 
        {activeTab === 1 && !initialLoading && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2, mt:0.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" required error={!bulkWarehouseId && !initialLoading}>
                        <InputLabel>{t('stock.quickAdd.fields.commonWarehouse')}</InputLabel>
                        <Select value={bulkWarehouseId || ''} onChange={(e) => { const whId = e.target.value as string; setBulkWarehouseId(whId); const wh = warehouses.find(w => w.id === whId); setSelectedWarehouse(wh || null); }} label={t('stock.quickAdd.fields.commonWarehouse')}>
                        {warehouses.map((wh) => (<MenuItem key={`bulk-${wh.id}`} value={wh.id}>{wh.name} {wh.isDefault && `(${t('stock.quickAdd.default')})`}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth required size="small" type="number" label={t('stock.quickAdd.fields.commonVatRate')} value={bulkVatRate} onChange={(e) => setBulkVatRate(e.target.value === '' ? '' : Number(e.target.value))} InputProps={{endAdornment: <InputAdornment position="end">%</InputAdornment>}} error={bulkVatRate === ''} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('stock.quickAdd.fields.commonVatStatus')}</InputLabel>
                        <Select value={bulkVatStatus} onChange={(e) => setBulkVatStatus(e.target.value)} label={t('stock.quickAdd.fields.commonVatStatus')}>
                        <MenuItem value="included">{t('stock.quickAdd.vatIncluded')}</MenuItem>
                        <MenuItem value="excluded">{t('stock.quickAdd.vatExcluded')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="number" label={t('stock.quickAdd.fields.commonDepartmentNo')} value={bulkDepartmentNo} onChange={(e) => {const value = e.target.value; if (value === '' || (Number(value) >= 1 && Number(value) <= 10)) {setBulkDepartmentNo(value === '' ? '' : Number(value));}}} InputProps={{inputProps: { min: 1, max: 10, step: 1}}} />
                </Grid>
                 <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mt: -1 }}>
                    <FormControlLabel control={<Checkbox checked={canBeNegativeBulk} onChange={(e) => setCanBeNegativeBulk(e.target.checked)} />} label={t('stock.quickAdd.fields.canBeNegativeAll')} />
                </Grid>
            </Grid>
            
            {bulkItemEntries.map((entry, index) => (
              <Grid container spacing={1} key={index} sx={{ mb: 1.5, alignItems: 'center' }}>
                <Grid item xs={0.5} sx={{pl: '4px !important'}}><Typography variant="body2" color="textSecondary" textAlign="center">{index + 1}.</Typography></Grid>
                <Grid item xs={3.5}>
                  <TextField fullWidth size="small" label={`${t('stock.quickAdd.fields.productName')} ${index + 1}`} value={entry.itemName} onChange={(e) => handleBulkItemChange(index, 'itemName', e.target.value)} />
                </Grid>
                <Grid item xs={2.5}>
                  <TextField fullWidth size="small" type="number" label={t('stock.quickAdd.fields.salePrice')} value={entry.salePrice} onChange={(e) => handleBulkItemChange(index, 'salePrice', e.target.value)} InputProps={{endAdornment: (<InputAdornment position="end">{getCurrencySymbol(saleCurrency)}</InputAdornment>)}}/>
                </Grid>
                <Grid item xs={2.5}>
                  <TextField fullWidth size="small" type="number" label={t('stock.quickAdd.fields.stockQuantity')} value={entry.quantity} onChange={(e) => handleBulkItemChange(index, 'quantity', e.target.value)} InputProps={{ inputProps: { min: 0 } }} />
                </Grid>
                <Grid item xs={3}>
                  <TextField fullWidth size="small" label={t('stock.quickAdd.fields.barcode')} value={entry.barcode || ''} onChange={(e) => handleBulkItemChange(index, 'barcode', e.target.value)} />
                </Grid>
              </Grid>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, borderTop: '1px solid #eee', mt: 'auto' }}>
        <Button onClick={() => handleClose(false)} variant="outlined" size="small">{t('stock.common.cancel')}</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          color={activeTab === 0 ? 'secondary' : 'primary'}
          size="small"
          disabled={loading || initialLoading || 
            (activeTab === 0 && (!itemName.trim() || !warehouseId || vatRate === '' || salePrice === '' || quantitySingle === '')) ||
            (activeTab === 1 && (!bulkWarehouseId || bulkVatRate === '' || bulkItemEntries.every(e => e.itemName.trim() === '' || e.salePrice === '' || e.quantity === '') ))
          }
        >
          {loading ? (
            <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> {t('stock.common.saving')}</>
          ) : (
            activeTab === 0 ? t('stock.quickAdd.actions.quickSave') : t('stock.quickAdd.actions.quickSaveAll')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddItemModal; 